import {
  put,
  takeLatest,
  call,
  actionChannel,
  take,
  fork,
  all,
} from 'redux-saga/effects'
import fetchFromAPI, { getDefaultAutorizedHeaders } from 'helpers/fetchFromApi'
import * as ApiRoute from 'constants/apiRouting'
import UrlHelper from 'helpers/url'
import NoticeHelper from 'helpers/notice'
import { SHOW_NOTICE } from 'reducers/notice'
import Psfs from 'lib/psfs/Psfs'
import { ACTION_DELETE, ACTION_DUPLICATE } from 'constants/index'
import { TYPE_SELECT } from 'helpers/jobs'
import {
  FETCH_JOB_LIST,
  SUCCESS_JOB_LIST,
  FAILURE_JOB_LIST,
  FETCH_JOB_TASK_LIST,
  SUCCESS_TASK_LIST,
  FAILURE_TASK_LIST,
  LOAD_TASK_LIST,
  LOAD_JOB_LIST,
  LOAD_EVT_JOB_TASK,
  SAVE_JOB_TASK_INPUT,
  UPDATE_JOB_TASK_INPUT,
  SAVE_JOB_MULTI_TASK_INPUTS,
  UPDATE_JOB_MULTI_TASK_INPUTS,
  CREATE_EVT_JOB_TASK,
  CREATE_EVT_JOB_MULTI_TASK,
  UPDATE_JOB_ITEM,
  DELETE_JOB_ITEM,
  DELETE_TASK_ITEM,
  FETCH_WORKFLOW_LIST,
  SUCCESS_WORKFLOW_LIST,
  FAILURE_WORKFLOW_LIST,
  FETCH_WORKFLOW_INFO,
  SUCCESS_WORKFLOW_INFO,
  FAILURE_WORKFLOW_INFO,
  CREATE_JOB,
  UPDATE_JOB_TASK,
  FETCH_ASSIGN_DATA,
  SUCCESS_ASSIGN_DATA,
  FAILURE_ASSIGN_DATA,
  LOAD_ASSIGN_DATA,
  UPDATE_TASK_ASSIGNEES,
  FETCH_JOB_FILTERS_DATA,
  SUCCESS_JOB_FILTERS_DATA,
  FAILURE_JOB_FILTERS_DATA,
  ADD_DYNAMIC_OPTION,
  DUPLICATE_INPUT_GROUP,
  DELETE_DUPLICATED_INPUT_GROUP,
  UPDATE_TASK_ASSIGNEES_SUCCESS,
  UPDATE_TASK_ASSIGNEES_FAILURE,
  EVALUATE_CALCULATION_FIELDS,
  CREATE_JOB_SUCCESS,
  CREATE_JOB_FAILURE,
  FETCH_LOOKUP_TABLE_SOURCE_DATA,
  FETCH_LOOKUP_TABLE_SOURCE_DATA_SUCCESS,
  FETCH_LOOKUP_TABLE_SOURCE_DATA_FAILURE,
  HANDLE_DEPENDENT_LOOKUP_INPUTS,
  SUCCESS_TASK_UPDATE,
  DELETE_INPUT_PREVIEWS,
  UPDATE_JOB_LOAD,
  LOAD_JOB_TASK_INPUT,
  SAVE_DUPLICATED_GROUP_ID,
  REMOVE_DUPLICATED_GROUP_ID,
  UPLOAD_FILE_FROM_DATA_URL,
  SET_EVT_INPUTS_RESULTS,
  SET_EVT_INPUT_RESULT,
  FETCH_JOB_REPORT_PDF_SUCCESS,
  FETCH_JOB_REPORT_PDF_FAILURE,
  FETCH_JOB_REPORT_PDF,
} from './reducer'

function* fetchJobList({ payload: { filters, params, skipLoader }, promise }) {
  try {
    if (!skipLoader) {
      yield put({ type: LOAD_JOB_LIST, payload: true })
    }

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.createQuery(
        `${ApiRoute.MY_JOB_TABLE}${UrlHelper.getPsfsQuery(filters)}`,
        params,
      ),
    })

    if (!response.hasOwnProperty('error')) {
      yield put({
        type: SUCCESS_JOB_LIST,
        payload: {
          response: response.data,
          page: filters.psfs.paginationObj.page,
        },
      })
    } else {
      yield put({ type: FAILURE_JOB_LIST })
    }

    if (!skipLoader) {
      yield put({ type: LOAD_JOB_LIST, payload: false })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('GET JOB LIST ERROR', error)
  }
}

function* fetchJobFiltersData({ payload, promise }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.createQuery(ApiRoute.MY_JOB_FILTERS, payload),
    })

    if (!response.hasOwnProperty('error')) {
      yield put({ type: SUCCESS_JOB_FILTERS_DATA, payload: response })
    } else {
      yield put({ type: FAILURE_JOB_FILTERS_DATA })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('FETCH JOB FILTERS DATA ERROR ', error)
  }
}

function* updateJob({ payload, promise }) {
  const { params, action } = payload

  try {
    yield put({ type: UPDATE_JOB_LOAD, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.MY_JOBS_UPDATE_JOB, {
        ...params,
        action,
      }),
      method: 'PUT',
      data: params,
    })

    if (response.errors) {
      const errorText = Object.values(response.errors)

      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(errorText),
      })
    }

    if (response && response.message) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(response.message),
      })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }

    yield put({ type: UPDATE_JOB_LOAD, payload: false })
  } catch (error) {
    console.log('UPDATE JOB FAILURE ', error)
  }
}

function* deleteJob({ payload, promise }) {
  const { id, series, data } = payload
  try {
    const singleOrSeriesUrl = series ? `${id}/job-series` : id
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.MY_JOBS}/${singleOrSeriesUrl}`,
      method: 'DELETE',
      data,
    })

    if (response && !response.error) {
      const isSeries = series ? 'Series ' : ''
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(`Job ${isSeries}was deleted`),
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(response.message),
      })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('DELETE JOB FAILURE ', error)
  }
}

function* getWorkflows({ payload: { activeSite } = {} }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.MY_JOBS_WORKFLOWS}${
        activeSite ? `?site=${activeSite}` : ''
      }`,
    })

    if (response && !response.error) {
      yield put({ type: SUCCESS_WORKFLOW_LIST, payload: response })
    } else {
      yield put({ type: FAILURE_WORKFLOW_LIST })
    }
  } catch (error) {
    console.log('FETCH WORKFLOW LIST FAILURE ', error)
  }
}

function* getWorkflowsInfo({ payload: { id, activeSite }, promise }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.MY_JOBS_WORKFLOWS}/${id}${
        activeSite ? `?site=${activeSite}` : ''
      }`,
    })

    if (response && !response.error) {
      yield put({ type: SUCCESS_WORKFLOW_INFO, payload: response })
    } else {
      yield put({ type: FAILURE_WORKFLOW_INFO })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('FETCH WORKFLOW INFO FAILURE ', error)
  }
}

function* createJob({ payload, promise }) {
  const { form, errorMessage } = payload

  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRoute.MY_JOBS,
      method: 'POST',
      data: form,
    })

    if (response.error) {
      yield put({ type: CREATE_JOB_FAILURE })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(errorMessage),
      })
    }

    yield put({ type: CREATE_JOB_SUCCESS })

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('ADD WORKFLOW FAILURE ', error)
  }
}

function* fetchJobTaskList({ payload: { activeJobId, skipLoader }, promise }) {
  try {
    if (!skipLoader) {
      yield put({ type: LOAD_TASK_LIST, payload: true })
    }

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.MY_JOBS}/${activeJobId}`,
    })

    if (!response.hasOwnProperty('error')) {
      yield put({
        type: SUCCESS_TASK_LIST,
        payload: response,
      })
    } else {
      yield put({ type: FAILURE_TASK_LIST })
    }

    if (!skipLoader) {
      yield put({ type: LOAD_TASK_LIST, payload: false })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('GET TASK INFO ERROR ', error)
  }
}

function* fetchAssignData({ payload: { extraFields = [] } = {} }) {
  try {
    yield put({ type: LOAD_ASSIGN_DATA, payload: true })

    const psfs = new Psfs()
      .addFields(['id', 'name', ...extraFields])
      .addFilter('enabled', true)
    const groupList = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.USER_GROUPS}${UrlHelper.getPsfsQuery({ psfs })}`,
    })
    const userList = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.USERS}${UrlHelper.getPsfsQuery({ psfs })}`,
    })

    if (
      !groupList.hasOwnProperty('error') &&
      !userList.hasOwnProperty('error')
    ) {
      yield put({
        type: SUCCESS_ASSIGN_DATA,
        payload: { groupList, userList },
      })
    } else {
      yield put({ type: FAILURE_ASSIGN_DATA })
    }

    yield put({ type: LOAD_ASSIGN_DATA, payload: false })
  } catch (error) {
    console.log('GET TASK INFO ERROR ', error)
  }
}

function* saveJobTaskInput({ payload, promise }) {
  try {
    yield put({ type: LOAD_JOB_TASK_INPUT, payload: true })

    const {
      data: { result, input, evt_task, updatedGroup },
      currentTaskID,
      currentEvtTaskID,
    } = payload
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRoute.MY_JOBS_EVENT_LIST,
      method: 'POST',
      data: { result, input, evt_task },
    })

    if (response && !response.error) {
      yield put({
        type: SET_EVT_INPUT_RESULT,
        payload: {
          currentTaskID,
          currentEvtTaskID,
          evtInputId: response.id,
          inputId: input,
          result,
          conditionalItems: response.conditionalItems,
          populatedEvtInputs: response.conditionalItems?.populatedEvtInputs,
          clearedPopulatedEvtInputIds:
            response.conditionalItems?.clearedPopulatedEvtInputIds,
        },
      })
    }

    if (response.message !== 'Validation failed') {
      yield put({
        type: SHOW_NOTICE,
        notice:
          response.error &&
          NoticeHelper.createErrorObj('Job task create failed!'),
      })
    }

    yield put({ type: LOAD_JOB_TASK_INPUT, payload: false })

    if (updatedGroup) {
      yield put({ type: REMOVE_DUPLICATED_GROUP_ID, payload: updatedGroup })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('JOB TASK SAVE FAILED ', error)
  }
}

function* updateJobTaskInput({ payload, promise }) {
  try {
    yield put({ type: LOAD_JOB_TASK_INPUT, payload: true })

    const {
      data: { result, evtInputId, updatedGroup, input },
      currentTaskID,
      currentEvtTaskID,
    } = payload
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.MY_JOBS_EVENT_LIST}/${evtInputId}`,
      method: 'PUT',
      data: { result },
    })

    if (response.message !== 'Validation failed') {
      yield put({
        type: SHOW_NOTICE,
        notice:
          response.error && NoticeHelper.createErrorObj('Task update failed!'),
      })
    }

    if (response && !response.error) {
      yield put({
        type: SET_EVT_INPUT_RESULT,
        payload: {
          currentTaskID,
          currentEvtTaskID,
          evtInputId,
          inputId: input,
          result,
          conditionalItems: response.conditionalItems,
          populatedEvtInputs: response.conditionalItems?.populatedEvtInputs,
          clearedPopulatedEvtInputIds:
            response.conditionalItems?.clearedPopulatedEvtInputIds,
        },
      })
    }

    yield put({ type: LOAD_JOB_TASK_INPUT, payload: false })

    if (updatedGroup) {
      yield put({ type: REMOVE_DUPLICATED_GROUP_ID, payload: updatedGroup })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('JOB TASK UPDATE FAILED ', error)
  }
}

function* saveJobMultiTaskInputs({ payload, promise }) {
  try {
    const {
      data: { result, input, data },
      currentTaskID,
    } = payload
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRoute.MY_JOBS_MULTI_EVENT_LIST,
      method: 'POST',
      data: { result, input, data },
    })

    if (response.message !== 'Validation failed') {
      yield put({
        type: SHOW_NOTICE,
        notice:
          response.error &&
          NoticeHelper.createErrorObj('Job tasks create failed!'),
      })
    }

    if (response && !response.error) {
      yield put({
        type: SET_EVT_INPUTS_RESULTS,
        payload: {
          currentTaskID,
          currentEvtTaskID: data[0]?.evt_task,
          evtInputs: response,
          inputId: input,
          result,
        },
      })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('JOB TASKS SAVE FAILED ', error)
  }
}

function* updateJobMultiTaskInputs({ payload, promise }) {
  try {
    const { data, currentTaskID, currentEvtTask, inputId } = payload
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRoute.MY_JOBS_MULTI_EVENT_LIST,
      method: 'PUT',
      data,
    })

    if (response.message !== 'Validation failed') {
      yield put({
        type: SHOW_NOTICE,
        notice:
          response.error && NoticeHelper.createErrorObj('Tasks update failed!'),
      })
    }

    if (response && !response.error) {
      yield put({
        type: SET_EVT_INPUTS_RESULTS,
        payload: {
          currentTaskID,
          currentEvtTaskID: currentEvtTask,
          evtInputs: data.evt_inputs,
          inputId,
          result: data.result,
        },
      })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('JOB TASKS UPDATE FAILED ', error)
  }
}

function* createEvtJobMultiTask({ payload, promise }) {
  try {
    const { data } = payload
    yield put({ type: LOAD_EVT_JOB_TASK, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRoute.MY_JOBS_TASKS_MULTI_EVENTS,
      method: 'POST',
      data,
    })

    if (response && response.errors) {
      const errorText = Object.values(response.errors)

      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(errorText),
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice:
          response.error &&
          NoticeHelper.createErrorObj('Job multi task create failed!'),
      })
    }

    yield put({ type: LOAD_EVT_JOB_TASK, payload: false })

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('JOB MULTI TASK CREATE FAILED ', error)
  }
}

function* createEvtJobTask({ payload, promise }) {
  try {
    const { data } = payload
    yield put({ type: LOAD_EVT_JOB_TASK, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRoute.MY_JOBS_TASKS_EVENTS,
      method: 'POST',
      data,
    })

    if (response && response.errors) {
      const errorText = Object.values(response.errors)

      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(errorText),
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice:
          response.error &&
          NoticeHelper.createErrorObj('Job task create failed!'),
      })
    }

    yield put({ type: LOAD_EVT_JOB_TASK, payload: false })

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('JOB TASK CREATE FAILED ', error)
  }
}

function* updateJobTask({ payload, promise }) {
  try {
    yield put({ type: UPDATE_JOB_LOAD, payload: true })

    const { params, action } = payload

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.MY_JOBS_UPDATE_TASK, {
        ...params,
        action,
      }),
      method: 'PUT',
    })

    if (response.errors) {
      const errorText = Object.values(response.errors)

      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(errorText),
      })
    } else if (response) {
      yield put({
        type: SUCCESS_TASK_UPDATE,
        payload: {
          jobId: params.jobId,
          action,
        },
      })

      if (response.message) {
        yield put({
          type: SHOW_NOTICE,
          notice: NoticeHelper.createErrorObj(response.message),
        })
      }
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }

    yield put({ type: UPDATE_JOB_LOAD, payload: false })
  } catch (error) {
    console.log('UPDATE TASK FAILURE ', error)
  }
}

function* deleteTask({
  payload: { id, jobId, previewsToRemove = [] },
  promise,
}) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.MY_JOB_TASKS}/${id}`,
      method: 'DELETE',
    })

    if (response && !response.error) {
      yield put({
        type: SUCCESS_TASK_UPDATE,
        payload: {
          jobId,
          action: ACTION_DELETE,
        },
      })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj('Task was successfully deleted'),
      })

      if (previewsToRemove.length) {
        yield put({
          type: DELETE_INPUT_PREVIEWS,
          payload: {
            previewsToRemove,
          },
        })
      }
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(response.message),
      })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('DELETE TASK FAILURE ', error)
  }
}

function* updateTaskAssignees({ payload, promise }) {
  const { isSeriesJob, params, form, successMessage, errorMessage } = payload

  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(
        isSeriesJob
          ? ApiRoute.MY_JOBS_UPDATE_SERIAL_TASK_ASSIGNEES
          : ApiRoute.MY_JOBS_UPDATE_TASK_ASSIGNEES,
        params,
      ),
      method: 'PUT',
      data: form,
    })

    if (response && !response.error) {
      yield put({
        type: UPDATE_TASK_ASSIGNEES_SUCCESS,
      })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
    } else {
      const { errors } = response
      const message = errors && errors.job_task ? errors.job_task : errorMessage

      yield put({
        type: UPDATE_TASK_ASSIGNEES_FAILURE,
      })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(message),
      })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    yield put({
      type: UPDATE_TASK_ASSIGNEES_FAILURE,
    })
    console.log('JOB TASK UPDATE FAILED ', error)
  }
}

function* addDynamicOption({ payload, promise }) {
  try {
    const { id, data } = payload

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.MY_JOBS_ADD_DYNAMIC_OPTION, { id }),
      method: 'POST',
      data,
    })

    if (response && !response.error) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj('New option added'),
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(response.message),
      })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('ADD DINAMIC OPTION FAILED ', error)
  }
}

function* duplicateInputGroup({ payload, promise }) {
  try {
    const { id, action = ACTION_DUPLICATE, data } = payload

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.JOB_INPUT_GROUPS_DUPLICATE, {
        id,
        action,
      }),
      method: 'PUT',
      data,
    })

    if (response && !response.error) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj('Input Group duplicated!'),
      })

      yield put({
        type: SAVE_DUPLICATED_GROUP_ID,
        payload: response,
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(response.message),
      })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('DUPLICATE INPUT GROUP FAILED ', error)
  }
}

function* deleteDuplicatedInputGroup({
  payload: { id, previewsToRemove = [] },
  promise,
}) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.JOB_INPUT_GROUPS_ITEM, { id }),
      method: 'DELETE',
    })

    if (response && !response.error) {
      if (previewsToRemove.length) {
        yield put({
          type: DELETE_INPUT_PREVIEWS,
          payload: {
            previewsToRemove,
          },
        })
      }

      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(
          'Duplicated Input Group deleted!',
        ),
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(response.message),
      })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('DELETING DUPLICATED INPUT GROUP FAILED ', error)
  }
}

function* evaluateCalculationFields({ payload, promise }) {
  try {
    const responseArr = []

    for (const input of payload) {
      const { inputId, evtTaskIds } = input
      let evtTaskQuery = ''

      evtTaskIds.forEach((id, index) => {
        evtTaskQuery += `&evt_tasks[${index}]=${id}`
      })

      const response = yield call(fetchFromAPI, {
        headers: getDefaultAutorizedHeaders(),
        partUrl: `${UrlHelper.create(
          ApiRoute.MY_JOBS_EVALUATE_CALCULATION_FIELD,
          { inputId },
        )}${evtTaskQuery}`,
      })

      if (response && !response.error) {
        responseArr.push({
          inputId,
          response,
        })
      }
    }

    yield call(promise.resolve, responseArr)
  } catch (error) {
    console.log('EVALUATING CALCULATION FIELD FAILED ', error)
  }
}

function* fetchLookupTableSourceData({
  payload: { inputId: inputItemId, evtTask, taskId, evtInputId },
  promise,
}) {
  try {
    yield put({ type: LOAD_TASK_LIST, payload: true })

    const evtInput = evtInputId ? `&evt_input=${evtInputId}` : ''
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${UrlHelper.create(
        `${ApiRoute.MY_JOBS_LOOKUP_TABLE_SOURCE_DATA}`,
        {
          id: inputItemId,
        },
      )}?evt_task=${evtTask}${evtInput}`,
    })

    if (!response.hasOwnProperty('error')) {
      const { options, ordered_choices } = response
      yield put({
        type: FETCH_LOOKUP_TABLE_SOURCE_DATA_SUCCESS,
        payload: {
          options,
          orderedChoices: ordered_choices,
          inputItemId,
          taskId,
        },
      })
    } else {
      yield put({ type: FETCH_LOOKUP_TABLE_SOURCE_DATA_FAILURE })
    }

    yield put({ type: LOAD_TASK_LIST, payload: false })

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.log('FETCH LOOKUP TABLE SOURCE ERROR', error)
  }
}

function* handleDependentLookupInputs({ payload, promise }) {
  try {
    yield put({ type: LOAD_TASK_LIST, payload: true })
    const { data: inputsData, currentTaskID } = payload

    for (const data of inputsData) {
      const { inputId, data: evtTasksData, evt_inputs, type } = data

      if (evtTasksData.length) {
        const evtInputQueryParam = evt_inputs.length
          ? `&evt_input=${evt_inputs[0]}`
          : ''
        const response = yield call(fetchFromAPI, {
          headers: getDefaultAutorizedHeaders(),
          partUrl: `${UrlHelper.create(
            `${ApiRoute.MY_JOBS_LOOKUP_TABLE_SOURCE_DATA}`,
            {
              id: inputId,
            },
          )}?evt_task=${evtTasksData[0].evt_task}${evtInputQueryParam}`,
        })

        if (response && !response.error) {
          const result =
            response.value !== undefined
              ? response.value
              : Object.values(response.options)[0]
          if (evt_inputs.length && type !== TYPE_SELECT) {
            yield call(updateJobMultiTaskInputs, {
              payload: {
                data: {
                  result,
                  evt_inputs,
                },
                currentTaskID,
                currentEvtTask: evtTasksData[0].evt_task,
                inputId,
              },
            })
          } else if (type !== TYPE_SELECT) {
            yield call(saveJobMultiTaskInputs, {
              payload: {
                data: {
                  result,
                  input: inputId,
                  data: evtTasksData,
                },
                currentTaskID,
              },
            })
          }
        }
      }
    }

    yield put({ type: LOAD_TASK_LIST, payload: false })

    if (promise && promise.resolve) {
      yield call(promise.resolve)
    }
  } catch (error) {
    console.log('FETCH LOOKUP TABLE SOURCE ERROR', error)
  }
}

function* sagaWatcher(action, fn) {
  const requestChannel = yield actionChannel(action)

  while (true) {
    const action = yield take(requestChannel)
    yield call(fn, action)
  }
}

function* uploadFileFromDataUrl({ payload, promise }) {
  try {
    const { file } = payload

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRoute.MY_JOBS_UPLOAD_FILE,
      method: 'POST',
      data: { file },
    })

    if (!response || response.error) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(response.message),
      })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
  } catch (error) {
    console.error('FILE UPLOAD FAILED ', error)
  }
}

function* fetchJobReportPdfSaga({
  payload: { jobId, reportTemplateId },
  promise,
}) {
  try {
    const pdfBlob = yield call(fetchFromAPI, {
      headers: {
        ...getDefaultAutorizedHeaders(),
        Accept: 'application/pdf',
      },
      partUrl: UrlHelper.create(ApiRoute.JOB_GENERATE_REPORT, {
        job: jobId,
        jobToReportTemplate: reportTemplateId,
      }),
      responseType: 'blob',
    })

    // on success, store blob in state
    yield put({
      type: FETCH_JOB_REPORT_PDF_SUCCESS,
      payload: pdfBlob,
    })

    // resolve promise if passed
    if (promise && promise.resolve) {
      yield call(promise.resolve, pdfBlob)
    }
  } catch (error) {
    // on failure, save error
    yield put({
      type: FETCH_JOB_REPORT_PDF_FAILURE,
      payload: error.message || 'Failed to download PDF',
    })

    if (promise && promise.reject) {
      yield call(promise.reject, error)
    }
  }
}

export function* jobsSagaChannels() {
  yield all([
    fork(sagaWatcher, UPDATE_JOB_TASK_INPUT, updateJobTaskInput),
    fork(sagaWatcher, SAVE_JOB_TASK_INPUT, saveJobTaskInput),
    fork(
      sagaWatcher,
      HANDLE_DEPENDENT_LOOKUP_INPUTS,
      handleDependentLookupInputs,
    ),
  ])
}

function* myJobsSaga() {
  yield takeLatest(FETCH_JOB_LIST, fetchJobList)
  yield takeLatest(FETCH_JOB_FILTERS_DATA, fetchJobFiltersData)
  yield takeLatest(UPDATE_JOB_ITEM, updateJob)
  yield takeLatest(DELETE_JOB_ITEM, deleteJob)
  yield takeLatest(DELETE_TASK_ITEM, deleteTask)
  yield takeLatest(FETCH_WORKFLOW_LIST, getWorkflows)
  yield takeLatest(FETCH_WORKFLOW_INFO, getWorkflowsInfo)
  yield takeLatest(CREATE_JOB, createJob)
  yield takeLatest(FETCH_JOB_TASK_LIST, fetchJobTaskList)
  yield takeLatest(SAVE_JOB_MULTI_TASK_INPUTS, saveJobMultiTaskInputs)
  yield takeLatest(UPDATE_JOB_MULTI_TASK_INPUTS, updateJobMultiTaskInputs)
  yield takeLatest(CREATE_EVT_JOB_TASK, createEvtJobTask)
  yield takeLatest(CREATE_EVT_JOB_MULTI_TASK, createEvtJobMultiTask)
  yield takeLatest(UPDATE_JOB_TASK, updateJobTask)
  yield takeLatest(FETCH_ASSIGN_DATA, fetchAssignData)
  yield takeLatest(UPDATE_TASK_ASSIGNEES, updateTaskAssignees)
  yield takeLatest(ADD_DYNAMIC_OPTION, addDynamicOption)
  yield takeLatest(DUPLICATE_INPUT_GROUP, duplicateInputGroup)
  yield takeLatest(DELETE_DUPLICATED_INPUT_GROUP, deleteDuplicatedInputGroup)
  yield takeLatest(EVALUATE_CALCULATION_FIELDS, evaluateCalculationFields)
  yield takeLatest(FETCH_LOOKUP_TABLE_SOURCE_DATA, fetchLookupTableSourceData)
  yield takeLatest(UPLOAD_FILE_FROM_DATA_URL, uploadFileFromDataUrl)
  yield takeLatest(FETCH_JOB_REPORT_PDF, fetchJobReportPdfSaga)
}

export default myJobsSaga
